import type { TravelMode, Waypoint } from "@/features/routebuilder/types";

export type { TravelMode, Waypoint };

// ── Raw parsed GPX data ───────────────────────────────────────────────────────

export interface GpxTrackPoint {
  lat: number;
  lng: number;
  ele: number | null;       // metres above sea level
  time: string | null;      // ISO 8601 timestamp
  distFromStart: number;    // cumulative metres from track start (computed)
}

export interface GpxRoute {
  name: string;
  trkpts: GpxTrackPoint[];
  totalDistanceM: number;
  elevationGainM: number;
  elevationLossM: number;
  durationSec: number | null; // null when no time data in GPX
}

// ── Settings ──────────────────────────────────────────────────────────────────

export interface GpxRouteSettings {
  routeName: string;
  description: string;
  travelMode: TravelMode;
  routeColor: string;            // hex, default "#6366f1"
  routeWidth: number;            // 2–8, default 3
  routeDash: boolean;
  showElevation: boolean;
  proximityRadiusM: number;      // 5–500, default 50
  myPhotosEnabled: boolean;
  communityPhotosEnabled: boolean;
  simplifyTolerance: number;     // 0 = off; max ~0.0003 degrees (~30m)
  trimStart: number;             // fraction 0–1
  trimEnd: number;               // fraction 0–1
}

export const DEFAULT_GPX_SETTINGS: GpxRouteSettings = {
  routeName: "My GPX Route",
  description: "",
  travelMode: "driving",
  routeColor: "#6366f1",
  routeWidth: 3,
  routeDash: false,
  showElevation: true,
  proximityRadiusM: 50,
  myPhotosEnabled: true,
  communityPhotosEnabled: false,
  simplifyTolerance: 0,
  trimStart: 0,
  trimEnd: 1,
};

// ── Persisted route ───────────────────────────────────────────────────────────

export interface SavedGpxRoute {
  id: string;
  name: string;
  settings: GpxRouteSettings;
  rawTrkpts: GpxTrackPoint[];  // full resolution, pre-trim
  waypoints: Waypoint[];
  createdAt: string;
}

// ── Proximity search result ───────────────────────────────────────────────────

export interface NearbyRouteItem {
  id: string;
  name: string | null;
  base_tile_url: string | null;
  lat: number;
  lng: number;
  dist_m: number;
  route_fraction: number;
}

export const GPX_STORAGE_KEY = "gpx_routes_saved";
